import { getNoise } from './synth';

/**
 * Background ambience: wind over open country.
 *
 * Continuous rather than looped, so there is no seam to hear. The bed is two
 * layers of filtered noise plus a very quiet low drone, each modulated by slow
 * oscillators running at deliberately unrelated periods (23s, 31s, 17s). Because
 * those periods never line up, the result never settles into a pattern the ear
 * can latch onto, which is what separates ambience from a loop.
 *
 * It is meant to sit under everything and go unnoticed. If you can pick it out
 * without listening for it, it is too loud.
 */

interface Bed {
  body: BiquadFilterNode;
  air: GainNode;
  drone: GainNode;
  nodes: AudioScheduledSourceNode[];
}

let bed: Bed | null = null;
let running = false;

/** Slow sine LFO wired to modulate an AudioParam. */
function lfo(
  ctx: AudioContext,
  target: AudioParam,
  periodSeconds: number,
  depth: number,
  phase = 0,
): OscillatorNode {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 1 / periodSeconds;

  const amount = ctx.createGain();
  amount.gain.value = depth;
  osc.connect(amount).connect(target);

  // Start part way through the cycle so the layers do not all begin together.
  osc.start(ctx.currentTime + phase);
  return osc;
}

export function startAmbience(ctx: AudioContext, out: AudioNode): void {
  if (bed || running) return;
  running = true;

  const nodes: AudioScheduledSourceNode[] = [];
  const noise = getNoise(ctx);

  // --- body: the low push of wind ------------------------------------------
  const bodySource = ctx.createBufferSource();
  bodySource.buffer = noise;
  bodySource.loop = true;

  const body = ctx.createBiquadFilter();
  body.type = 'lowpass';
  body.frequency.value = 420;
  body.Q.value = 0.7;

  const bodyGain = ctx.createGain();
  bodyGain.gain.value = 0.5;

  bodySource.connect(body).connect(bodyGain).connect(out);
  bodySource.start();
  nodes.push(bodySource);

  // Breathe: the filter opens and closes, and the level swells with it.
  nodes.push(lfo(ctx, body.frequency, 23, 220));
  nodes.push(lfo(ctx, bodyGain.gain, 31, 0.22, 0.4));

  // --- air: a thin band well above the body --------------------------------
  const airSource = ctx.createBufferSource();
  airSource.buffer = noise;
  airSource.loop = true;

  const airFilter = ctx.createBiquadFilter();
  airFilter.type = 'bandpass';
  airFilter.frequency.value = 2400;
  airFilter.Q.value = 0.8;

  const air = ctx.createGain();
  air.gain.value = 0.035;

  airSource.connect(airFilter).connect(air).connect(out);
  airSource.start();
  nodes.push(airSource);

  nodes.push(lfo(ctx, airFilter.frequency, 17, 700, 1.1));
  nodes.push(lfo(ctx, air.gain, 29, 0.022, 2.3));

  // --- drone: weight, felt more than heard ---------------------------------
  const droneOsc = ctx.createOscillator();
  droneOsc.type = 'sine';
  droneOsc.frequency.value = 58;

  const drone = ctx.createGain();
  drone.gain.value = 0.03;

  droneOsc.connect(drone).connect(out);
  droneOsc.start();
  nodes.push(droneOsc);
  nodes.push(lfo(ctx, drone.gain, 41, 0.014, 3.7));

  bed = { body, air, drone, nodes };
}

export function stopAmbience(): void {
  if (!bed) return;
  for (const node of bed.nodes) {
    try {
      node.stop();
    } catch {
      // Already stopped.
    }
  }
  bed = null;
  running = false;
}

export type AmbienceScene = 'village' | 'map' | 'menu' | 'silence';

/**
 * Tilt the bed to suit the screen. The village sits in a sheltered clearing,
 * so it is darker and quieter; the map is open country and gets more air.
 * A slow ramp keeps the change from registering as an event.
 */
export function setAmbienceScene(ctx: AudioContext, scene: AmbienceScene): void {
  if (!bed) return;
  const now = ctx.currentTime;
  const glide = 2.5;

  const settings: Record<AmbienceScene, { body: number; air: number; drone: number }> = {
    village: { body: 360, air: 0.022, drone: 0.034 },
    map: { body: 520, air: 0.05, drone: 0.024 },
    menu: { body: 300, air: 0.018, drone: 0.03 },
    // Signed out. The login page is a door, not a place: nothing breathes
    // behind it until the player steps through.
    silence: { body: 300, air: 0, drone: 0 },
  };

  const target = settings[scene];
  bed.body.frequency.setTargetAtTime(target.body, now, glide);
  bed.air.gain.setTargetAtTime(target.air, now, glide);
  bed.drone.gain.setTargetAtTime(target.drone, now, glide);
}
